/**
 * Manufacturing cell-formatting presets for `DataTable` / `VirtualTable`.
 *
 * Every module that renders KPIs in a table previously reimplemented the same
 * formatting + threshold-coloring logic (FPY %, OEE %, cycle-time duration,
 * cost, equipment state, model confidence). That drifted: one module rounded
 * yield to one decimal, another to none; one colored < 95 % red, another
 * < 90 %. These presets centralize the decision so every grid reads the same.
 *
 * This file is the **pure decision layer** — it returns a `FormattedCell`
 * descriptor ({ text, tone, title }) with no React, so the threshold logic is
 * unit-testable in the `node --test` sweep. The `.tsx` companion
 * (`table-formatters.tsx`) renders these descriptors into `DataTableColumn.cell`
 * render functions. Modules normally import the rendering helpers; the resolver
 * is exported for tests and for callers that want the raw tone/text.
 */

/**
 * Semantic tone of a formatted value. Maps onto the design-token color set used
 * by `Badge` / `StatusBadge`, so a "bad" KPI reads red and a "good" one green
 * consistently across modules.
 */
export type CellTone =
  | 'neutral'
  | 'good'
  | 'warning'
  | 'critical'
  | 'info';

/** Pure result of formatting a value — text + semantic tone + hover title. */
export interface FormattedCell {
  /** Display string (already rounded / suffixed / localized). */
  text: string;
  /** Semantic tone for threshold coloring. */
  tone: CellTone;
  /** Optional hover title (full-precision value, raw state, etc.). */
  title?: string;
}

const NA: FormattedCell = { text: '—', tone: 'neutral' };

function isNil(v: unknown): v is null | undefined {
  return v === null || v === undefined;
}

function toNumber(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

// ─────────────────────────────────────────────────────────────────────────
// Percentage with threshold coloring (FPY, OEE, yield, utilization, …).
// ─────────────────────────────────────────────────────────────────────────

export interface PercentageFormatOptions {
  /** Decimal places (default 1). */
  precision?: number;
  /**
   * Set when the source value is a fraction in 0–1 rather than 0–100. The
   * formatter multiplies by 100 before rendering.
   */
  fromFraction?: boolean;
  /**
   * Lower bound (inclusive) for the `good` tone. At/above this → good. Default
   * 95. Pass `warnBelow`/`criticalBelow` for the full ladder.
   */
  goodAtOrAbove?: number;
  /** At/above this (but below `goodAtOrAbove`) → warning. Default 90. */
  warnAtOrAbove?: number;
  /**
   * Invert the ladder for "lower is better" KPIs (scrap %, defect rate): a
   * small value is good, a large value is critical.
   */
  lowerIsBetter?: boolean;
}

/**
 * Resolve a percentage value to text + threshold tone. The default ladder
 * (good ≥ 95, warning ≥ 90, else critical) matches the plant-floor FPY/OEE
 * convention; override the bounds per KPI.
 */
export function resolvePercentage(
  value: unknown,
  opts: PercentageFormatOptions = {}
): FormattedCell {
  const raw = toNumber(value);
  if (raw === null) return NA;
  const {
    precision = 1,
    fromFraction = false,
    goodAtOrAbove = 95,
    warnAtOrAbove = 90,
    lowerIsBetter = false,
  } = opts;
  const pct = fromFraction ? raw * 100 : raw;
  const text = `${pct.toFixed(precision)}%`;

  let tone: CellTone;
  if (lowerIsBetter) {
    // For "lower is better", the bounds describe the *bad* side: at/above the
    // warn bound is concerning, at/above the good bound is critical.
    if (pct >= goodAtOrAbove) tone = 'critical';
    else if (pct >= warnAtOrAbove) tone = 'warning';
    else tone = 'good';
  } else {
    if (pct >= goodAtOrAbove) tone = 'good';
    else if (pct >= warnAtOrAbove) tone = 'warning';
    else tone = 'critical';
  }
  return { text, tone, title: `${pct.toFixed(Math.max(precision, 2))}%` };
}

// ─────────────────────────────────────────────────────────────────────────
// Duration (cycle time, downtime, MTBF/MTTR) — humanized from seconds.
// ─────────────────────────────────────────────────────────────────────────

export interface DurationFormatOptions {
  /** Source unit of the numeric value (default 'seconds'). */
  unit?: 'milliseconds' | 'seconds' | 'minutes' | 'hours';
  /** Max number of unit segments to show (default 2, e.g. "1h 12m"). */
  maxSegments?: number;
  /** Color critical above this many seconds (e.g. downtime SLA). */
  criticalAboveSeconds?: number;
  /** Color warning above this many seconds. */
  warnAboveSeconds?: number;
}

const UNIT_TO_SECONDS: Record<NonNullable<DurationFormatOptions['unit']>, number> = {
  milliseconds: 1 / 1000,
  seconds: 1,
  minutes: 60,
  hours: 3600,
};

/** Resolve a duration to a humanized string + optional SLA threshold tone. */
export function resolveDuration(
  value: unknown,
  opts: DurationFormatOptions = {}
): FormattedCell {
  const raw = toNumber(value);
  if (raw === null) return NA;
  const { unit = 'seconds', maxSegments = 2, criticalAboveSeconds, warnAboveSeconds } =
    opts;
  const totalSeconds = raw * UNIT_TO_SECONDS[unit];
  const text = humanizeDuration(totalSeconds, maxSegments);

  let tone: CellTone = 'neutral';
  if (criticalAboveSeconds !== undefined && totalSeconds >= criticalAboveSeconds) {
    tone = 'critical';
  } else if (warnAboveSeconds !== undefined && totalSeconds >= warnAboveSeconds) {
    tone = 'warning';
  }
  return { text, tone, title: `${totalSeconds.toFixed(0)} s` };
}

/** Humanize seconds into "1d 3h" / "2h 5m" / "45s" with a segment cap. */
export function humanizeDuration(totalSeconds: number, maxSegments = 2): string {
  if (!Number.isFinite(totalSeconds)) return '—';
  const negative = totalSeconds < 0;
  let s = Math.abs(Math.round(totalSeconds));
  const units: Array<[label: string, secs: number]> = [
    ['d', 86400],
    ['h', 3600],
    ['m', 60],
    ['s', 1],
  ];
  const segs: string[] = [];
  for (const [label, secs] of units) {
    if (segs.length >= maxSegments) break;
    if (s >= secs || (segs.length === 0 && label === 's')) {
      const n = Math.floor(s / secs);
      if (n > 0 || (segs.length === 0 && label === 's')) {
        segs.push(`${n}${label}`);
        s -= n * secs;
      }
    }
  }
  if (segs.length === 0) segs.push('0s');
  return (negative ? '-' : '') + segs.join(' ');
}

// ─────────────────────────────────────────────────────────────────────────
// Currency (cost, variance, journal amounts).
// ─────────────────────────────────────────────────────────────────────────

export interface CurrencyFormatOptions {
  /** ISO 4217 currency code (default 'USD'). */
  currency?: string;
  /** BCP-47 locale (default 'en-US'). */
  locale?: string;
  /** Decimal places (default 2). */
  precision?: number;
  /**
   * Color a *signed* amount: negative → critical (over budget / loss),
   * positive → good. Off by default (most amounts are neutral).
   */
  signedTone?: boolean;
}

/** Resolve a monetary amount to a locale-formatted string + optional tone. */
export function resolveCurrency(
  value: unknown,
  opts: CurrencyFormatOptions = {}
): FormattedCell {
  const raw = toNumber(value);
  if (raw === null) return NA;
  const { currency = 'USD', locale = 'en-US', precision = 2, signedTone = false } =
    opts;
  let text: string;
  try {
    text = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: precision,
      maximumFractionDigits: precision,
    }).format(raw);
  } catch {
    // Unknown currency code / locale — fall back to a plain fixed string.
    text = `${raw.toFixed(precision)} ${currency}`;
  }
  let tone: CellTone = 'neutral';
  if (signedTone) {
    if (raw < 0) tone = 'critical';
    else if (raw > 0) tone = 'good';
  }
  return { text, tone };
}

// ─────────────────────────────────────────────────────────────────────────
// Numeric precision (counts, measurements) with optional unit + thresholds.
// ─────────────────────────────────────────────────────────────────────────

export interface NumericFormatOptions {
  precision?: number;
  /** Suffix appended after a space (e.g. "mm", "kg", "pcs"). */
  unit?: string;
  /** BCP-47 locale for grouping separators (default 'en-US'). */
  locale?: boolean | string;
  criticalAbove?: number;
  warnAbove?: number;
  criticalBelow?: number;
  warnBelow?: number;
}

/** Resolve a number to fixed precision with optional unit + threshold tone. */
export function resolveNumeric(
  value: unknown,
  opts: NumericFormatOptions = {}
): FormattedCell {
  const raw = toNumber(value);
  if (raw === null) return NA;
  const { precision = 0, unit, locale, criticalAbove, warnAbove, criticalBelow, warnBelow } =
    opts;
  let body: string;
  if (locale) {
    const loc = typeof locale === 'string' ? locale : 'en-US';
    body = new Intl.NumberFormat(loc, {
      minimumFractionDigits: precision,
      maximumFractionDigits: precision,
    }).format(raw);
  } else {
    body = raw.toFixed(precision);
  }
  const text = unit ? `${body} ${unit}` : body;

  let tone: CellTone = 'neutral';
  if (criticalAbove !== undefined && raw >= criticalAbove) tone = 'critical';
  else if (criticalBelow !== undefined && raw <= criticalBelow) tone = 'critical';
  else if (warnAbove !== undefined && raw >= warnAbove) tone = 'warning';
  else if (warnBelow !== undefined && raw <= warnBelow) tone = 'warning';
  return { text, tone };
}

// ─────────────────────────────────────────────────────────────────────────
// State badge (ISA-88 / ISA-95 / arbitrary lifecycle states).
// ─────────────────────────────────────────────────────────────────────────

const STATE_TONE: Record<string, CellTone> = {
  // ISA-88 equipment states
  idle: 'neutral',
  running: 'good',
  paused: 'warning',
  held: 'warning',
  stopped: 'critical',
  stopping: 'warning',
  complete: 'good',
  aborting: 'critical',
  aborted: 'critical',
  error: 'critical',
  maintenance: 'info',
  offline: 'neutral',
  // ISA-95 work order / batch states
  draft: 'neutral',
  created: 'neutral',
  scheduled: 'info',
  released: 'info',
  inprogress: 'good',
  completed: 'good',
  closed: 'neutral',
  cancelled: 'critical',
  // common pass/fail dispositions
  pass: 'good',
  passed: 'good',
  ok: 'good',
  fail: 'critical',
  failed: 'critical',
  reject: 'critical',
  rejected: 'critical',
  hold: 'warning',
  open: 'info',
};

/** Resolve a lifecycle / disposition state string to a tone. */
export function resolveState(
  value: unknown,
  toneOverrides?: Record<string, CellTone>
): FormattedCell {
  if (isNil(value) || value === '') return NA;
  const text = String(value);
  const key = text.replace(/[\s_-]/g, '').toLowerCase();
  const tone =
    (toneOverrides && toneOverrides[text]) ??
    (toneOverrides && toneOverrides[key]) ??
    STATE_TONE[key] ??
    'neutral';
  return { text, tone };
}

// ─────────────────────────────────────────────────────────────────────────
// Confidence gradient (vision / predictive / AI model scores).
// ─────────────────────────────────────────────────────────────────────────

export interface ConfidenceFormatOptions {
  /** Source value is 0–1 rather than 0–100 (default true). */
  fromFraction?: boolean;
  /** Decimal places of the rendered % (default 0). */
  precision?: number;
  /** At/above this confidence → good (default 90). */
  goodAtOrAbove?: number;
  /** At/above this → warning, below → critical (default 70). */
  warnAtOrAbove?: number;
}

/**
 * Resolve a model-confidence score to a % string + tone. Low confidence is
 * surfaced as `critical` so an operator knows the AI suggestion needs review.
 */
export function resolveConfidence(
  value: unknown,
  opts: ConfidenceFormatOptions = {}
): FormattedCell {
  const raw = toNumber(value);
  if (raw === null) return NA;
  const { fromFraction = true, precision = 0, goodAtOrAbove = 90, warnAtOrAbove = 70 } =
    opts;
  const pct = fromFraction ? raw * 100 : raw;
  const clamped = Math.max(0, Math.min(100, pct));
  const text = `${clamped.toFixed(precision)}%`;
  let tone: CellTone;
  if (clamped >= goodAtOrAbove) tone = 'good';
  else if (clamped >= warnAtOrAbove) tone = 'warning';
  else tone = 'critical';
  return { text, tone, title: `confidence ${clamped.toFixed(1)}%` };
}
